import styled, { css } from 'styled-components'

import { Button, Card } from '@baseV2'
import { Devices, Spacings, Text } from '@styles'
import { H3 } from '@base'

// reused from the Leads pages — grid whose column count is driven by itemPerRow: [mobile, tablet, desktop]
export { GridListWrapper } from '@pages/Admin/Lead/ManageLead/styles'

export const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B} 0;
`

export const SummaryRow = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(3, 1fr);
		gap: ${Spacings.SPACING_4B};
	}
`

export const SectionCard = styled(Card)`
	gap: ${Spacings.SPACING_3B};
`

export const ItemsCard = styled(Card)`
	flex-grow: 1;
`

export const ItemsHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
`

// one order item: read-only details on the left, the editable weight in a highlighted panel on the right
export const ItemRowCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};

	@media ${Devices.tablet} {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: ${Spacings.SPACING_4B};
	}
`

// itemPerRow: [mobile, tablet] count of fields in a row. On tablet the first
// column (product name) takes double width, the rest share equally.
export const ItemDetails = styled.div`
	display: grid;
	grid-template-columns: ${({ itemPerRow }) => `repeat(${itemPerRow?.[0] ?? 2}, minmax(0, 1fr))`};
	align-items: flex-start;
	gap: ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		flex-grow: 1;
		grid-template-columns: ${({ itemPerRow }) => `1.5fr repeat(${Math.max((itemPerRow?.[1] ?? 6) - 1, 1)}, minmax(0, 1fr))`};
		gap: ${Spacings.SPACING_3B};
	}
`

export const ItemProductName = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
	word-break: break-word;
`

export const ItemSubText = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.colors.text.secondary};
	text-transform: uppercase;
`

export const ItemEditableSection = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B};
	// border-radius: ${({ theme }) => theme.radius.md};
	// background: ${({ theme }) => theme.colors.brand.subtle};

	@media ${Devices.tablet} {
		min-width: calc(12 * ${Spacings.SPACING_4B});
	}
`

export const FooterRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
	background: ${({ theme }) => theme.colors.surface.card};
`
export const DetailField = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	grid-column: ${({ span }) => (span ? `span ${span}` : 'auto')};
`

// sizes match the Buyer PO order-details card (BuyerOrderDetails/components/OrderDetailsCard.jsx)
export const DetailLabel = styled.span`
	font-size: ${Text.BASE_SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	font-weight: ${Text.BOLD_400};
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};

	@media ${Devices.tablet} {
		font-size: ${Text.BASE_SMALL};
	}
`

export const DetailValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ color, theme }) => color || theme.colors.text.primary};
	word-break: break-word;
	display: ${({ inlineFlex }) => (inlineFlex ? 'inline-flex' : 'flex')};
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_1B};
	cursor: ${({ onClick }) => (onClick ? 'pointer' : 'inherit')};
	transition: opacity 0.15s ease;

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
	}

	&:hover {
		opacity: ${({ onClick }) => (onClick ? 0.8 : 1)};
	}
`

// --- Create/Edit Shipment layout ---
// Page shell: fills the outlet area and never grows past it, so the sections scroll
// inside it and the action bar sits on its bottom edge.
export const CreateShipmentPage = styled.div`
	display: flex;
	flex-direction: column;
	/* fills the height its tab column hands down, so the action bar lands on the bottom edge */
	flex: 1;
	min-height: 0;
	min-width: 0;
	gap: ${Spacings.SPACING_3B};
	overflow-x: hidden;
`

// Action bar pinned to the bottom edge of its own page shell. The Shipment tab's own pages hand
// their actions to the tab shell instead (so the bar spans the whole tab); this is still used by
// Add Dispatch Plan, which brings its own page wrapper.
export const StickyFooter = styled.div`
	position: sticky;
	bottom: 0;
	flex: 0 0 auto;
	margin-top: auto;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	}
`

// 12-column grid: left section spans 4, right spans the remaining 8 on laptop+.
// Below laptop it collapses to a single full-width column (right section stacks under the left).
// `align-items: stretch` keeps both sections the height of the taller one.
// It takes the space the footer leaves and scrolls within it, which is what keeps the
// footer on the bottom edge whatever the content length.
export const CreateShipmentWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	align-items: stretch;
	/* stacked below laptop: rows take their content height instead of stretching to fill the page */
	align-content: start;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	gap: ${Spacings.SPACING_2B};
	/* keeps the last card off the action bar */
	padding-bottom: ${Spacings.SPACING_3B};
	min-width: 0;

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;

	@media ${Devices.laptop} {
		grid-template-columns: repeat(12, minmax(0, 1fr));
		align-content: stretch;
		gap: ${Spacings.SPACING_3B};
	}
`

// Create Fulfillment's grid. Stacked below laptop, the left section keeps its content height and
// the right one takes whatever height is left (and more, if its content needs it — the grid
// scrolls). From laptop up it is the one-row side-by-side grid of CreateShipmentWrapper.
export const CreateFulfillmentWrapper = styled(CreateShipmentWrapper)`
	grid-template-rows: auto 1fr;

	@media ${Devices.laptop} {
		grid-template-rows: none;
	}
`

// Same surface treatment as the Supplier PO cards — card background, hairline border, rounded corners.
const ShipmentSection = styled.div`
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	padding: ${Spacings.SPACING_3B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	min-width: 0;
	grid-column: span 1;
	max-height: 100%;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_4B};
	}

	/* stacked below laptop, so a section takes the height it needs and the grid scrolls;
	   side by side it gets capped so each one scrolls on its own */
	@media ${Devices.laptop} {
		max-height: 76vh;
		overflow-y: auto;
		-ms-overflow-style: none;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}
`

export const LeftSection = styled(ShipmentSection)`
	@media ${Devices.laptop} {
		grid-column: span 3;
	}
`

export const RightSection = styled(ShipmentSection)`
	@media ${Devices.laptop} {
		grid-column: span 9;
	}
`

// Right section of the Edit Shipment page — same grid placement as RightSection, but with no
// surface of its own (no background, border or padding): the cards inside it carry that.
export const EditShipmentRightSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	min-width: 0;
	grid-column: span 1;
	max-height: 100%;

	@media ${Devices.laptop} {
		grid-column: span 9;
		max-height: 76vh;
	}
`

// A card that sits in a section without growing to fill it: the same surface, padding and gap as
// LeftSection, and as tall as its content.
// $maxHeight (any css length, default none) caps it from laptop up only, where the sections sit side
// by side and each scrolls on its own — past the cap the block scrolls within itself. Below laptop
// the sections stack and the page scrolls, so the block always takes its full content height.
// A percentage resolves only against a parent of definite height, e.g. a stretched grid item such
// as EditShipmentRightSection.
export const SectionBlock = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	gap: ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	padding: ${Spacings.SPACING_3B};
	min-width: 0;
	height: fit-content;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_4B};
	}

	@media ${Devices.laptop} {
		flex-shrink: 1;
		max-height: ${({ $maxHeight }) => $maxHeight ?? 'none'};
		min-height: 0;
		overflow-y: auto;
	}

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`

export const SectionHeading = styled(H3)`
	margin: 0;
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
`

// --- Right section: shipment tab strip ---
// Tabs on the left, Autofill pinned to the right of the same row.
export const RightSectionHeader = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_3B};
`

export const TabStrip = styled.div`
	display: flex;
	flex: 1;
	min-width: 0;
	align-items: stretch;
	gap: ${Spacings.SPACING_3B};
	overflow-x: auto;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;

	@media ${Devices.tablet} {
		gap: ${Spacings.SPACING_4B};
	}
`

// Shared base for the tab buttons. Both are ButtonV2 with the ghost/neutral variant, so the
// button's own fill, radius and padding are stripped back to a plain underline tab.
const shipmentTabStyles = css`
	flex: 0 0 auto;
	padding: ${Spacings.SPACING_2B} 0;
	border: none;
	border-radius: 0;
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	white-space: nowrap;

	/* the ghost variant tints its background on hover — the tab only changes colour */
	&:hover:not(:disabled) {
		background: none;
	}
`

// underline tabs: the active one is brand-coloured with a 2px rule under it
export const ShipmentTab = styled(Button).attrs({ variant: 'ghost', color: 'neutral', size: 'small' })`
	${shipmentTabStyles}
	color: ${({ theme, $selected }) => ($selected ? theme.colors.brand.primary : theme.colors.text.secondary)};
	border-bottom: 2px solid ${({ theme, $selected }) => ($selected ? theme.colors.brand.primary : 'transparent')};

	&:hover:not(:disabled) {
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

// Cross next to the active tab's label. It sits inside the tab button, so it can't be a button
// itself — it is a span that stops the click from reaching the tab.
export const TabCloseIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin-left: ${Spacings.SPACING_1B};
	color: inherit;
	cursor: pointer;
	transition: color 0.2s;

	& > svg {
		font-size: ${Text.MEDIUM};
	}

	&:hover {
		color: ${({ theme }) => theme.colors.status.error.text};
	}
`

// Button already supplies the disabled treatment (opacity + not-allowed cursor).
export const AddShipmentTab = styled(Button).attrs({ variant: 'ghost', color: 'neutral', size: 'small' })`
	${shipmentTabStyles}
	/* a lone "+" needs more size than a word to read as a control */
	font-size: ${Text.X_LARGE};
	line-height: 1;
	color: ${({ theme }) => theme.colors.text.secondary};
	border-bottom: 2px solid transparent;

	&:hover:not(:disabled) {
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

// holds whatever the selected tab renders — fills the rest of the right section
export const TabPanel = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
`

// --- Right section: shipment form ---
// Scrolls within the tab panel rather than growing the page; the scrollbar itself is hidden.
export const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	gap: ${Spacings.SPACING_4B};
	padding-top: ${Spacings.SPACING_4B};
	overflow-y: auto;

	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`

// Dates on the left, the shipment total pills pushed to the right edge of the same row.
export const FormHeaderRow = styled.div`
	display: flex;
	align-items: flex-end;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_3B};
`

// Bordered pill holding the shipment totals, divided by hairlines — same treatment as the
// summary bar on the Supplier PO create page (CreateSPO/v2/styles.js).
export const SummaryBar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${Spacings.SPACING_5B};
	margin-left: auto;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.sm};

	@media ${Devices.laptop} {
		flex-wrap: nowrap;
	}
`

export const SummaryItem = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	padding-right: ${Spacings.SPACING_2B};
	border-right: 1px solid ${({ theme }) => theme.colors.border.default};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	white-space: nowrap;

	&:last-child {
		border-right: none;
		padding-right: 0;
	}

	& > strong {
		font-weight: ${Text.BOLD_500};
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

// The row takes half the section's width at every breakpoint and stays left aligned.
// Its fields share that width equally: stacked on mobile, side by side from tablet up.
export const FormRow = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	align-items: flex-start;
	justify-content: flex-start;
	gap: ${Spacings.SPACING_4B};
	width: 50%;

	@media ${Devices.tablet} {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`
